import { useCallback } from 'react';

import { type DocNode } from '@atlaskit/adf-schema';
import type { RovoApp } from '@atlassian/conversation-assistant-chat-prompt-input/controllers/chat-mode-store/types';
import { useChatModeState } from '@atlassian/conversation-assistant-chat-prompt-input/useChatModeState';
import { encodeAdf } from '@atlassian/conversation-assistant-content-transformer/adf-encoder';
import { type InteractionSource } from '@atlassian/conversation-assistant-instrumentation/constants/analytics';
import { NO_PARENT_MESSAGE_ID } from '@atlassian/conversation-assistant-service/services/assistance-service/utils/data';
import { useChatMessagesActions } from '@atlassian/conversation-assistant-store/controllers/chat/use-chat-messages';
import { useUserPreferences } from '@atlassian/conversation-assistant-store/controllers/user-preferences';

import { getLastAgentMessageID } from '../chat-store/getLastAgentMessageID';
import { useChatStoreActions } from '../chat-store/useChatStoreActions';
import { useConversation } from '../conversation/useConversation';

export type ResendMessageOptions = {
	mode?: 'resend' | 'edit';
};

/**
 * Hook that provides a handler for resending or editing the last human
 * message, reusing the chat-mode state of the current conversation.
 *
 */
export const useResendMessage = (
	conversationId: string,
	options?: ResendMessageOptions,
): ((prompt: string | DocNode, _errorData?: { message_template?: string }) => Promise<void>) => {
	const mode = options?.mode ?? 'resend';
	const isEditMode = mode === 'edit';
	const interactionSource: InteractionSource = isEditMode ? 'editMessage' : 'resendMessage';

	const { sendMessage, truncateFromLastHumanMessage } = useChatStoreActions();
	const { selectedAgent } = useConversation({ conversationId });
	const {
		preferences: { storeUserMessages },
	} = useUserPreferences();
	const currentChatMode = useChatModeState(conversationId);
	const { getMessages } = useChatMessagesActions();

	return useCallback(
		async (prompt: string | DocNode, _errorData?: { message_template?: string }) => {
			let webSearchEnabled: boolean | undefined;
			let deepResearchEnabled: boolean | undefined;
			let thinkDeeperEnabled: boolean | undefined;
			let fastModeEnabled: boolean | undefined;
			let taskModeEnabled: boolean | undefined;
			let useCurrentPageContext: boolean | undefined;
			let appFilters: RovoApp[] | undefined;

			webSearchEnabled = currentChatMode.webSearchEnabled;
			deepResearchEnabled = currentChatMode.deepResearchEnabled;
			thinkDeeperEnabled = currentChatMode.thinkDeeperEnabled;
			fastModeEnabled = currentChatMode.fastModeEnabled;
			taskModeEnabled = currentChatMode.taskModeEnabled;
			useCurrentPageContext = currentChatMode.useCurrentPageContext;
			appFilters = currentChatMode.appFilters;

			// For edits, snapshot the hidden messages so `sendMessage` can
			// restore them via `editRollback` if the send fails terminally.
			const truncatedMessages = isEditMode
				? await truncateFromLastHumanMessage({ conversationId })
				: undefined;

			const targetParentMessageId = isEditMode
				? (getLastAgentMessageID(getMessages(conversationId)) ?? NO_PARENT_MESSAGE_ID)
				: undefined;

			void sendMessage({
				agent: selectedAgent,
				prompt: encodeAdf(prompt),
				conversationId,
				// TODO(ai_mate_store_messages_be_driven): remove this local storeUserMessages payload
				// value when BE user settings are fully rolled out; BE overrides it via the capability header.
				storeUserMessages,
				deepResearchEnabled,
				thinkDeeperEnabled,
				taskModeEnabled,
				fastModeEnabled,
				webSearchEnabled,
				addCurrentPageAsContextUserPreference: useCurrentPageContext,
				appFilters,
				messageSources: {
					interactionSource,
				},
				...(isEditMode && targetParentMessageId ? { parentMessageId: targetParentMessageId } : {}),
				...(truncatedMessages && truncatedMessages.length > 0
					? { editRollback: { restoreMessages: truncatedMessages } }
					: {}),
			});
		},
		[
			selectedAgent,
			conversationId,
			storeUserMessages,
			sendMessage,
			truncateFromLastHumanMessage,
			currentChatMode,
			isEditMode,
			getMessages,
			interactionSource,
		],
	);
};
